import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '../utils/cn';
import { Label } from './Label';
import { Check, ChevronDown, X } from 'lucide-react';

export interface ComboboxOption {
  /** The committed value — what `onChange` receives. */
  value: string;
  /** Primary display text. */
  label: string;
  /** Secondary text, rendered muted after the label (e.g. a technical id). */
  description?: string;
}

export interface ComboboxProps {
  label?: string;
  error?: string;
  hint?: string;
  options: ComboboxOption[];
  /** Committed value (controlled). Empty string means "nothing selected". */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * Let Enter commit typed text that matches no option. For pick-lists over
   * an authoritative set (a directory of tracker fields) leave this off —
   * the caller then never receives a value outside `options`. Turn it on
   * when the list is advisory and the true value space is open (a field id
   * the tracker's directory does not report).
   */
  allowCustomValue?: boolean;
  /** Render an × that commits the empty value. */
  clearable?: boolean;
  /** Accessible name for label-less placements (table cells, toolbars).
   * Prefer `label` wherever a visible label fits. */
  'aria-label'?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

/** Case-insensitive substring match over everything a row shows. */
function matches(option: ComboboxOption, query: string): boolean {
  const q = query.toLowerCase();
  return (
    option.label.toLowerCase().includes(q) ||
    option.value.toLowerCase().includes(q) ||
    (option.description?.toLowerCase().includes(q) ?? false)
  );
}

/**
 * A searchable single-select: a text input filtering a listbox, committing on
 * option click or Enter. Search matches label, value, AND description, so a
 * list of tracker fields is findable by human name and technical id alike.
 *
 * Deliberately a sibling of `Select`, not a mode on it: `Select` is a button
 * revealing a static list (closed vocabulary, few entries), this is an input
 * filtering a large one — they share tokens, not behaviour.
 *
 * The input's text is a *query* while the list is open and a *projection of
 * the committed value* while closed; Escape and outside-click discard the
 * query rather than committing it, so an abandoned search never becomes a
 * value.
 */
export function Combobox({
  className,
  label,
  error,
  hint,
  id,
  options,
  value,
  onChange,
  placeholder = 'Search…',
  allowCustomValue = false,
  clearable = false,
  'aria-label': ariaLabel,
  disabled = false,
}: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null); // null = not editing
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const comboboxId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const listboxId = `${comboboxId ?? 'combobox'}-listbox`;

  const selectedOption = options.find((o) => o.value === value);
  /** What the input shows when no query is being typed. */
  const committedText = selectedOption ? selectedOption.label : value;
  const inputText = query ?? committedText;

  const filtered = useMemo(() => {
    if (query == null || query.trim() === '') return options;
    return options.filter((o) => matches(o, query.trim()));
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      // Optional call: the test DOM (happy-dom) does not implement it.
      item?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  function commit(nextValue: string) {
    setIsOpen(false);
    setQuery(null);
    onChange(nextValue);
  }

  function handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(0);
        } else {
          setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter': {
        if (!isOpen) return; // let a wrapping form submit
        e.preventDefault();
        const highlighted = filtered[highlightedIndex];
        if (highlighted) {
          commit(highlighted.value);
        } else if (allowCustomValue && (query ?? '').trim() !== '') {
          commit((query ?? '').trim());
        }
        break;
      }
      case 'Escape':
        if (isOpen) {
          // Contain the key: a wrapping Dialog must not close on a keypress
          // that only meant "drop this search".
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          setQuery(null);
        }
        break;
    }
  }

  return (
    <div className="space-y-1.5" ref={containerRef}>
      {label && <Label htmlFor={comboboxId}>{label}</Label>}
      <div className="relative">
        <input
          ref={inputRef}
          id={comboboxId}
          role="combobox"
          type="text"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          value={inputText}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && highlightedIndex >= 0 && filtered[highlightedIndex]
              ? `${listboxId}-${highlightedIndex}`
              : undefined
          }
          className={cn(
            'w-full rounded-lg border px-3 py-2 text-sm',
            clearable && value !== '' ? 'pr-14' : 'pr-10',
            'bg-transparent text-text-primary placeholder:text-text-muted',
            'transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            error
              ? 'border-feedback-error focus:ring-feedback-error'
              : 'border-border-strong hover:border-border-input-hover',
            disabled && 'opacity-50 cursor-not-allowed',
            className,
          )}
          onFocus={() => {
            if (!disabled) {
              setIsOpen(true);
              setQuery('');
              setHighlightedIndex(-1);
            }
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(e.target.value.trim() === '' ? -1 : 0);
          }}
          onKeyDown={handleKeyDown}
        />
        {clearable && value !== '' && !disabled && (
          <button
            type="button"
            aria-label="Clear selection"
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-text-muted hover:text-text-primary"
            onClick={() => commit('')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <ChevronDown
          className={cn(
            'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
          aria-hidden="true"
        />
        {isOpen && (
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border-strong bg-bg-elevated shadow-lg py-1"
          >
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-text-muted" aria-disabled="true">
                {allowCustomValue ? 'No matches — press Enter to use the typed value.' : 'No matches.'}
              </li>
            )}
            {filtered.map((option, index) => {
              const isSelected = option.value === value;
              const isHighlighted = index === highlightedIndex;
              return (
                <li
                  key={option.value}
                  id={`${listboxId}-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    'px-3 py-2 text-sm cursor-pointer transition-colors',
                    'text-text-primary',
                    isHighlighted && 'bg-bg-subtle',
                    isSelected && !isHighlighted && 'bg-primary-500/10',
                    !isHighlighted && !isSelected && 'hover:bg-bg-subtle',
                  )}
                  // mousedown, not click: the outside-click closer runs on
                  // mousedown, and click would arrive after the list is gone.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(option.value);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {option.label}
                      {option.description && <span className="ml-2 text-xs text-text-muted">{option.description}</span>}
                    </span>
                    {isSelected && <Check className="h-4 w-4 text-primary-500 shrink-0" />}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {error && <p className="text-xs text-feedback-error">{error}</p>}
      {hint && !error && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

Combobox.displayName = 'Combobox';
